'use client';

import { c as _c } from "react/compiler-runtime";
import { createClient, errorExchange, fetchExchange, Provider, useMutation } from 'urql';
import { cacheExchange } from '@urql/exchange-graphcache';
import { retryExchange } from '@urql/exchange-retry';
import { refocusExchange } from '@urql/exchange-refocus';
import { type Dispatch, type ReactNode, type SetStateAction, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useTelegram } from '@/context/TelegramContext';
import { GraphQLError } from 'graphql/error';
import { copyTextToClipboard, hapticFeedback, popup } from '@tma.js/sdk';
import { closingBehavior, miniApp, useSignal } from '@tma.js/sdk-react';
import { Auth } from '@/graphql/mutations';
import SplashScreen from '@/components/screens/SplashScreen';
import LoadingScreen from '@/components/screens/LoadingScreen';
const httpStatusCode2GraphQLCode = (code: number) => {
  switch (code) {
    case 400:
      return 'BAD_USER_INPUT';
    case 401:
      return 'UNAUTHENTICATED';
    case 403:
      return 'FORBIDDEN';
    case 404:
      return 'NOT_FOUND';
    case 409:
      return 'CONFLICT';
    case 410:
      return 'GONE';
    case 423:
      return 'LOCKED';
    case 451:
      return 'UNAVAILABLE_FOR_LEGAL_REASONS';
    case 500:
      return 'INTERNAL_SERVER_ERROR';
    default:
      return `ERROR ${code.toString()}`;
  }
};
function AuthInitializer(t0) {
  const $ = _c(14);
  const {
    rawInitData,
    jwt,
    setJwt,
    setBanner
  } = t0;
  const [authResult, execAuth] = useMutation(Auth);
  let t1;
  let t2;
  if ($[0] !== execAuth || $[1] !== jwt || $[2] !== rawInitData) {
    t1 = () => {
      if (!jwt && rawInitData) {
        execAuth({});
      }
    };
    t2 = [execAuth, jwt, rawInitData];
    $[0] = execAuth;
    $[1] = jwt;
    $[2] = rawInitData;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] !== authResult.data || $[6] !== authResult.error || $[7] !== setJwt) {
    t3 = () => {
      if (!authResult.error && authResult.data) {
        setJwt(authResult.data.auth);
      }
    };
    $[5] = authResult.data;
    $[6] = authResult.error;
    $[7] = setJwt;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== authResult || $[10] !== setBanner || $[11] !== setJwt) {
    t4 = [authResult, setJwt, setBanner];
    $[9] = authResult;
    $[10] = setBanner;
    $[11] = setJwt;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  useEffect(t3, t4);
  let t5;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <LoadingScreen isBrowser={false} />;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}
export default function GraphQLProvider(t0) {
  const $ = _c(17);
  const {
    children
  } = t0;
  const {
    rawInitData
  } = useTelegram();
  const [jwt, setJwt] = useState(null);
  const [jwtExpired, setJwtExpired] = useState(false);
  const isPopupOpened = useSignal(popup.isOpened);
  const [banner, setBanner] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = error => {
      const code = typeof error === "number" ? httpStatusCode2GraphQLCode(error) : error.extensions?.code;
      bb10: switch (code) {
        case "UNAUTHENTICATED":
          {
            if (closingBehavior.isConfirmationEnabled()) {
              closingBehavior.disableConfirmation();
            }
            if (hapticFeedback.isSupported()) {
              hapticFeedback.notificationOccurred("error");
            }
            if (popup.isSupported()) {
              popup.show({
                title: "Authorization Error",
                message: "Your session has timed out. Please open this app again",
                buttons: [{
                  type: "close"
                }]
              });
            }
            setJwtExpired(true);
            break bb10;
          }
        case "FORBIDDEN":
          {
            setBanner(<div className="flex flex-col items-center justify-center-safe gap-4">{(typeof error === "number" || !error.message || !error.message.includes("Access denied")) && <><h1 className="text-center text-3xl">Access denied</h1><br /></>}{typeof error !== "number" && error.message && <><h1 className="text-center text-3xl">{error.message}</h1><br /></>}<h3 className="text-center text-xl">If you believe this is a mistake, disable your VPN/proxy or change its region/server</h3></div>);
            copyTextToClipboard(JSON.stringify(error));
            break bb10;
          }
        default:
          {
            copyTextToClipboard(JSON.stringify(error));
            if (hapticFeedback.isSupported()) {
              hapticFeedback.notificationOccurred("error");
            }
            if (popup.isSupported()) {
              popup.show({
                title: "Error",
                message: typeof error === "number" ? error.toString() : error.message,
                buttons: [{
                  type: "close"
                }]
              });
            }
          }
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleError = t1;
  let t2;
  let t3;
  if ($[1] !== isPopupOpened || $[2] !== jwtExpired) {
    t2 = () => {
      if (jwtExpired && !isPopupOpened && miniApp.isSupported()) {
        miniApp.close();
      }
    };
    t3 = [jwtExpired, isPopupOpened];
    $[1] = isPopupOpened;
    $[2] = jwtExpired;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== jwt || $[6] !== rawInitData) {
    let t5;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = error_0 => {
        if (error_0.graphQLErrors.length) {
          error_0.graphQLErrors.forEach(error_1 => handleError(error_1));
        } else {
          handleError((error_0.response as Response)?.status);
        }
      };
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    t4 = createClient({
      url: process.env.NEXT_PUBLIC_BACKEND_URL || "/",
      exchanges: [cacheExchange({
        updates: {
          Mutation: {
            setAvatar: _temp,
            transfer: _temp2
          }
        }
      }), errorExchange({
        onError: t5
      }), retryExchange({
        maxNumberAttempts: 3,
        randomDelay: true
      }), refocusExchange(), fetchExchange],
      fetchOptions: {
        headers: {
          authorization: jwt ? `Bearer ${jwt}` : `TMA ${rawInitData}`
        }
      },
      fetchSubscriptions: true,
      preferGetMethod: false,
      suspense: true
    });
    $[5] = jwt;
    $[6] = rawInitData;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const client = t4;
  let t5;
  if ($[9] !== banner || $[10] !== children || $[11] !== jwt || $[12] !== rawInitData) {
    t5 = banner ? <SplashScreen isBrowser={false}>{banner}</SplashScreen> : jwt ? <Suspense fallback={<LoadingScreen isBrowser={false} />}>{children}</Suspense> : <AuthInitializer rawInitData={rawInitData} jwt={jwt} setJwt={setJwt} setBanner={setBanner} />;
    $[9] = banner;
    $[10] = children;
    $[11] = jwt;
    $[12] = rawInitData;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== client || $[15] !== t5) {
    t6 = <Provider value={client}>{t5}</Provider>;
    $[14] = client;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}
function _temp2(result_0, _args_0, cache_0) {
  if (result_0.transfer) {
    cache_0.invalidate("Query", "me");
  }
}
function _temp(result, _args, cache) {
  if (result.setAvatar) {
    cache.invalidate("Query", "me");
  }
}